'use client'

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { sessionStore, ProjectSession } from '@/lib/session-store'

interface SessionContextValue {
  get(projectId: string): ProjectSession | null
  subscribe(projectId: string, cb: (s: ProjectSession | null) => void): () => void
  refresh(projectId: string, force?: boolean): Promise<ProjectSession>
  updateStep(projectId: string, stepNumber: number, payload: Record<string, any>): Promise<void>
  deleteStep(projectId: string, stepNumber: number): Promise<void>
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: { children: React.ReactNode }) {
  console.log('💾 SessionProvider: Rendering')
  
  const value = useMemo<SessionContextValue>(() => {
    console.log('💾 SessionProvider: Creating session context value')
    return {
      get: (projectId) => sessionStore.getSnapshot(projectId),
      subscribe: (projectId, cb) => sessionStore.subscribe(projectId, cb),
      refresh: (projectId, force) => sessionStore.refresh(projectId, force),
      updateStep: (projectId, stepNumber, payload) => sessionStore.updateStep(projectId, stepNumber, payload),
      deleteStep: (projectId, stepNumber) => sessionStore.deleteStep(projectId, stepNumber),
    }
  }, [])

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  )
}

export function useSessionStore(projectId?: string) {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSessionStore must be used within SessionProvider')

  const [snapshot, setSnapshot] = useState<ProjectSession | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true) // Always start loading until first update
  const [error, setError] = useState<string | null>(null)
  const [lastProjectId, setLastProjectId] = useState<string | undefined>(projectId)
  const [isInitializing, setIsInitializing] = useState(true)

  // Reset state when projectId changes
  useEffect(() => {
    if (projectId !== lastProjectId) {
      setLastProjectId(projectId)
      // Clear snapshot to avoid showing stale/previous data
      setSnapshot(null)
      setIsLoading(true) // Keep loading until first fresh snapshot
      setError(null)
      setIsInitializing(true)
    }
  }, [projectId, lastProjectId])

  useEffect(() => {
    if (!projectId) {
      setIsLoading(false)
      setIsInitializing(false)
      return
    }
    
    let isMounted = true
    let hasReceivedSnapshot = false
    
    const unsub = ctx.subscribe(projectId, (newSnapshot) => {
      if (!isMounted) return
      setSnapshot(newSnapshot)
      if (newSnapshot !== null) {
        hasReceivedSnapshot = true
        setIsLoading(false)
      }
      setIsInitializing(false)
      // Only clear loading when we have a real snapshot; keep loading true for null (e.g. initial emit before refresh) to avoid flashing "Project Not Found" on refresh
    })
    
    // Always trigger a background refresh; inflight dedupe prevents duplicate work
    ctx.refresh(projectId)
      .then(() => {
        if (!isMounted) return
        setIsInitializing(false)
        // Do NOT set isLoading(false) here when !hasReceivedSnapshot - the subscribe callback
        // will run when the store emits, and that is the only place we clear loading. This
        // avoids flashing "Project Not Found" when refresh completes before React has
        // processed the subscription update.
      })
      .catch(e => {
        if (!isMounted) return
        setError(e?.message || 'Failed to refresh')
        setIsLoading(false)
        setIsInitializing(false)
      })
    
    return () => {
      isMounted = false
      unsub()
    }
  }, [projectId, ctx])

  return {
    data: snapshot,
    project: snapshot?.project, // Expose full project details
    isLoading,
    error,
    refresh: async (force: boolean = false) => {
      if (!projectId) return
      setIsLoading(true)
      try {
        await ctx.refresh(projectId, force)
      } catch (e: any) {
        setError(e?.message || 'Failed to refresh')
      } finally {
        setIsLoading(false)
      }
    },
    updateStep: async (stepNumber: number, payload: Record<string, any>) => {
      if (!projectId) return
      await ctx.updateStep(projectId, stepNumber, payload)
    },
    deleteStep: async (stepNumber: number) => {
      if (!projectId) return
      await ctx.deleteStep(projectId, stepNumber)
    }
  }
}


